/**
 * SubscriptionRoute — Protège une route selon le niveau d'abonnement
 *
 * Si l'utilisateur tente d'accéder directement à une URL réservée à un abonnement
 * supérieur au sien, il est redirigé vers une page d'accès refusé plutôt que de
 * voir le contenu de la page.
 *
 * Usage dans App.jsx :
 *   <SubscriptionRoute page="dashboard" requiredSubscription="premium">
 *     <Dashboard />
 *   </SubscriptionRoute>
 */
import { useFeatureFlags } from '../hooks/useFeatureFlags.jsx';

/**
 * Page affichée quand l'utilisateur n'a pas le bon abonnement
 */
const UpgradePage = ({ requiredSubscription, currentSubscription }) => {
  const subscriptionLabels = {
    basic: 'Basic',
    standard: 'Standard',
    premium: 'Premium',
  };

  const subscriptionColors = {
    basic: 'bg-gray-100 text-gray-700 border-gray-300',
    standard: 'bg-blue-100 text-blue-700 border-blue-300',
    premium: 'bg-purple-100 text-purple-700 border-purple-300',
  };

  const subscriptionIcons = {
    basic: '🥉',
    standard: '🥈',
    premium: '🥇',
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-lg p-8 text-center">
        {/* Icône */}
        <div className="text-6xl mb-4">🔒</div>

        {/* Titre */}
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Accès restreint</h1>

        {/* Description */}
        <p className="text-gray-500 mb-6">
          Cette page n'est pas incluse dans votre abonnement actuel. Passez à l'offre supérieure pour y accéder.
        </p>

        {/* Comparaison abonnements */}
        <div className="flex items-center justify-center gap-3 mb-8">
          <div className="text-center">
            <div className="text-2xl mb-1">{subscriptionIcons[currentSubscription] || '⭐'}</div>
            <span
              className={`inline-block px-3 py-1 rounded-full text-xs font-semibold border ${
                subscriptionColors[currentSubscription] || subscriptionColors.basic
              }`}
            >
              {subscriptionLabels[currentSubscription] || currentSubscription?.toUpperCase()}
            </span>
            <p className="text-xs text-gray-400 mt-1">Votre offre</p>
          </div>

          <div className="text-gray-300 text-2xl">→</div>

          <div className="text-center">
            <div className="text-2xl mb-1">{subscriptionIcons[requiredSubscription] || '⭐'}</div>
            <span
              className={`inline-block px-3 py-1 rounded-full text-xs font-semibold border ${
                subscriptionColors[requiredSubscription] || subscriptionColors.premium
              }`}
            >
              {subscriptionLabels[requiredSubscription] || requiredSubscription?.toUpperCase()}
            </span>
            <p className="text-xs text-gray-400 mt-1">Requis</p>
          </div>
        </div>

        {/* Bouton retour */}
        <button
          onClick={() => window.history.back()}
          className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium py-2 px-4 rounded-lg transition-colors"
        >
          ← Retour
        </button>
      </div>
    </div>
  );
};

/**
 * Composant de route protégée par abonnement
 *
 * @param {string} page - Nom de la page tel que défini dans useFeatureFlags (ex: "dashboard")
 * @param {string} [requiredSubscription] - Abonnement minimum requis. Si non fourni,
 *   le composant utilise isPageEnabled() pour déterminer l'accès.
 * @param {React.ReactNode} children - Contenu de la route à afficher si accès autorisé
 */
const SubscriptionRoute = ({ page, requiredSubscription, children }) => {
  const { isPageEnabled, currentSubscription, isLoaded } = useFeatureFlags();

  // Pendant le chargement de la config, on affiche un spinner neutre
  if (!isLoaded) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mx-auto" />
          <p className="mt-3 text-sm text-gray-500">Vérification des droits d'accès...</p>
        </div>
      </div>
    );
  }

  // Vérification via isPageEnabled (utilise la config chargée depuis le backend)
  const hasAccess = isPageEnabled(page);

  if (!hasAccess) {
    // Déterminer l'abonnement requis pour l'affichage de la page d'upgrade
    const required = requiredSubscription || 'premium';
    return <UpgradePage requiredSubscription={required} currentSubscription={currentSubscription} pageName={page} />;
  }

  return children;
};

export default SubscriptionRoute;
